import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { usePromotions } from "@/query/promotionQuery"
import { useServicesQuery } from "@/query/serviceQuery"
import {
  addServiceItem,
  setSelectedCategory,
  setSelectedServiceId,
} from "@/store/cashRegisterReducers/cashRegisterSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { BookingServiceItem } from "@okeys/shared-utils"
import { Plus } from "lucide-react"
import React, { useMemo } from "react"

export const AddServiceSection: React.FC = () => {
  const dispatch = useAppDispatch()
  const { services, pendingServices, errorServices } = useServicesQuery()
  const { promotions, isLoadingPromotions, errorPromotions } = usePromotions()

  const { addServiceSectionInfo } = useAppSelector(
    (state) => state.cashRegister
  )

  const filteredServices = useMemo(() => {
    if (!addServiceSectionInfo?.selectedCategory || !services) return []
    return services.filter(
      (service) => service.category === addServiceSectionInfo.selectedCategory
    )
  }, [services, addServiceSectionInfo?.selectedCategory])

  const categories = useMemo(() => {
    if (!services) return []
    const uniqueCategories = Array.from(
      new Set(
        services
          .filter((service) => service.category)
          .map((service) => service.category)
      )
    )
    return uniqueCategories
  }, [services])

  if (
    isLoadingPromotions ||
    pendingServices ||
    errorPromotions ||
    errorServices
  ) {
    return <div>Chargement...</div>
  }
  if (errorPromotions || errorServices) {
    return <div>Erreur lors du chargement des données</div>
  }

  const handleAddService = () => {
    if (!addServiceSectionInfo?.selectedServiceId || !services) return

    const service = services.find(
      (s) => s.id === addServiceSectionInfo.selectedServiceId
    )
    if (!service) return

    const serviceItem: BookingServiceItem = {
      serviceId: addServiceSectionInfo.selectedServiceId,
      price: service.price || 0,
      taxInfo: {
        type: service.taxInfo.type,
        countryCode: service.taxInfo.countryCode,
      },
      walkInAdded: true,
      cancelled: false,
      discountApplied: null,
      name: service.name,
      metadata: null,
    }

    dispatch(
      addServiceItem({
        serviceItem,
        promotions: promotions || [],
        availableServices: services || [],
      })
    )
    dispatch(setSelectedServiceId(undefined))
    dispatch(setSelectedCategory(undefined))
  }

  const handleCategoryChange = (value: string) => {
    dispatch(setSelectedCategory(value))
  }

  const handleServiceChange = (value: string) => {
    dispatch(setSelectedServiceId(value))
  }

  return (
    <>
      <div className="space-y-4 border-1 border-border bg-card rounded-lg p-4">
        <h3 className="text-lg font-semibold text-card-foreground">
          Ajouter un service
        </h3>
        <div className="flex gap-4">
          <div className="space-y-2 flex-1">
            <Select
              value={addServiceSectionInfo?.selectedCategory || ""}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder="Sélectionner une catégorie"
                  className="text-card-foreground"
                />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sélecteur de service */}
          <div className="space-y-2 flex-1">
            <Select
              value={addServiceSectionInfo?.selectedServiceId || ""}
              onValueChange={handleServiceChange}
              disabled={!addServiceSectionInfo?.selectedCategory}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sélectionner un service" />
              </SelectTrigger>
              <SelectContent>
                {filteredServices.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    <div className="flex justify-between items-center w-full">
                      <span>{service.name}</span>
                      <span className="text-green-600 font-semibold ml-2">
                        {service.price.toFixed(2)}€
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bouton d'ajout */}
        <Button
          onClick={handleAddService}
          disabled={!addServiceSectionInfo?.selectedServiceId}
          className="w-full bg-primary hover:bg-primary/80"
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter le service
        </Button>
      </div>
    </>
  )
}
